import * as v from 'valibot';

import { routes } from '@/assets/constants/routes-no.ts';
import { ArrowLink } from '@/competitions/_common/components/ArrowLink.tsx';
import Page from '@/components/Page/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import { getActiveCompetition, competitionWinnersQuery } from '@/lib/api/requests.ts';
import { getQueryClient } from '@/lib/query-client-context.ts';
import { betterParse } from '@/lib/schema-utils.ts';
import { Timer, type TimerTheme } from '@/ui/Timer.tsx';

import { JUNE_2026_CAMPAIGN_END_DATE } from '../../config.ts';

import type { Route } from './+types/LandingPage';
import { CTA } from './_components/CTA.tsx';
import { TopSection } from './_sections/TopSection.tsx';

const dateFormatter = new Intl.DateTimeFormat('no', {
  day: 'numeric',
  month: 'long',
});
const dateStr = dateFormatter.format(JUNE_2026_CAMPAIGN_END_DATE);

const timerTheme: TimerTheme = {
  backgroundColor: '#FFFFFF',
  digitsColor: '#223C66',
  labelsColor: '#FFFFFF',
};

const MetaSchema = v.nullish(
  v.object({
    displayWarningWhenKIn: v.array(v.union([v.pipe(v.number(), v.toString()), v.string()])),
  }),
  () => undefined,
);

export const links: Route.LinksFunction = () => [...TopSection.links];

export async function loader({ context }: Route.LoaderArgs) {
  const queryClient = getQueryClient(context);
  const competitions = await queryClient.query(competitionWinnersQuery());
  const activeCompetition = await getActiveCompetition(competitions);
  const currentCompetition = competitions.find((c) => c.slug === 'june_2026_campaign');
  const allWinners = competitions.flatMap((d) => d.winners);
  const meta = betterParse(MetaSchema, currentCompetition?.meta).unwrap();

  return {
    activeCompetition,
    winners: currentCompetition?.winners,
    allWinners,
    displayWarningWhenKIn: meta?.displayWarningWhenKIn,
  };
}

export default function LandingPage({ loaderData }: Route.ComponentProps) {
  const { activeCompetition, winners, allWinners, displayWarningWhenKIn } = loaderData;

  return (
    <Page title="June-trekning">
      <div className="flex w-full flex-col gap-12 bg-white pb-16 lg:gap-16">
        <TopSection
          activeCompetition={activeCompetition}
          winners={winners}
          allWinners={allWinners}
          displayWarningWhenKIn={displayWarningWhenKIn}
        />

        {/* Mid section */}
        <section className="mx-auto flex w-full max-w-[1420px] flex-col items-center justify-between gap-8 px-4 md:flex-row-reverse md:items-start">
          <div className="flex w-full max-w-sm flex-1 flex-col overflow-hidden rounded-2xl bg-[#18A0FB] p-6 py-8 typography-v1-body2 text-white lg:max-w-md">
            <h2 className="typography-v1-h2 md:mb-6">Trekningsdato</h2>
            <h2 className="mb-4 typography-v1-display md:mb-12 lg:mb-16 lg:text-6xl">{dateStr}</h2>
            <Timer
              date={JUNE_2026_CAMPAIGN_END_DATE}
              theme={timerTheme}
              className="origin-left md:scale-110 lg:scale-125"
            />

            <p className="mt-4 mb-8 typography-v1-body3 md:mt-12">
              Vinnerne kontaktes via e-post og mobil.
              <br />
              Vi tar hensyn til personvern og publiserer ikke vinnere uten samtykke.
            </p>

            <ArrowLink
              className="mt-auto block typography-v1-body3 font-semibold text-white!"
              to={routes.JUNE_2026_COMPETITION_TERMS}
            >
              Vilkår og betingelser
            </ArrowLink>
          </div>

          <div className="flex max-w-sm flex-1 flex-col gap-4 md:max-w-lg">
            <h2 className="typography-v1-h1">Over halvparten får lavere rente</h2>
            <p className="typography-v1-body3">
              Over halvparten av de som sjekker med Scoopr får tilbud om lavere rente på minst ett
              av sine lån.
            </p>
            <p className="typography-v1-body3">
              Lavere rente gir mindre kostnader og bedre råd. Første steg er å få oversikt over din
              gjeld og dine renter.
            </p>
            <p className="mb-2 typography-v1-body3">
              Gjennom våre trekninger har tusenvis av nordmenn fått tilbud om lavere rente!
            </p>
            <CTA
              className="md:mr-auto"
              activeCompetition={activeCompetition}
              displayWarningWhenKIn={displayWarningWhenKIn}
              onClick={() => sendUserPortalEvent('jun26_competition_cta_middle_clicked')}
            />
          </div>
        </section>

        {/* Bottom section */}
        <section className="mx-auto flex min-h-[500px] w-full max-w-[1420px] flex-col items-center justify-between gap-8 px-4 md:flex-row-reverse md:items-start">
          <div className="flex w-full max-w-sm flex-col rounded-2xl bg-v1-background-60 p-6 py-8 typography-v1-body2 text-v1-background-200 lg:max-w-md">
            <h2 className="mb-2 typography-v1-h2">Slik fungerer Premiepotten</h2>
            <p className="mb-4">
              Vinneren får dekket sin kostnad ved å betjene sin rentebærende kredittkort- og/eller
              forbruksgjeld de neste 12 månedene.
            </p>

            <ul className="mb-16 list-disc pl-6">
              <li>
                Premie: Inntil 200 000 kr basert på beregnet gjeldskostnad de neste 12 månedene.
              </li>
              <li>Inkluderer: Rentebærende kredittkort- og forbruksgjeld.</li>
            </ul>

            <ArrowLink
              className="mt-auto block typography-v1-body3"
              to={routes.JUNE_2026_COMPETITION_TERMS}
            >
              Vilkår og betingelser
            </ArrowLink>
          </div>

          <div className="flex max-w-sm flex-1 flex-col gap-4 md:max-w-lg">
            <h2 className="typography-v1-h1">Hvordan fungerer det?</h2>
            <p className="typography-v1-body3">
              Scoopr hjelper deg med å refinansiere gjeld ved å hente inn tilbud fra flere banker på
              én gang. I stedet for å søke hos hver bank separat, sender du én søknad gjennom oss,
              så sammenligner vi ulike tilbud for å finne bedre renter og vilkår som passer din
              økonomi.
            </p>
            <p className="mb-2 typography-v1-body3">
              Refinansiering handler om å samle eksisterende lån og kreditter i ett nytt lån med
              bedre oversikt og lavere kostnader. Målet er å gjøre økonomien enklere å håndtere og
              gi deg mer kontroll over månedlige utgifter. Hos Scoopr gjør vi prosessen raskere,
              enklere og mer oversiktlig ved å koble deg med flere banker gjennom én løsning.
            </p>
            <CTA
              className="md:mr-auto"
              activeCompetition={activeCompetition}
              displayWarningWhenKIn={displayWarningWhenKIn}
              onClick={() => sendUserPortalEvent('jun26_competition_cta_bottom_clicked')}
            />
          </div>
        </section>
      </div>
    </Page>
  );
}
